import { DataPageModel } from "@modelve/api/models/dataPageModel";
import { PagedReportDataModel } from "@modelve/api/models/scenarioReportModels";
import { AxiosError, Method } from "axios";
import { useNotyf } from "contexts/NotyfContext";
import { useCallback, useEffect, useState } from "react";
import useSWR, { SWRConfiguration, SWRResponse, useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";
import { useApiStore } from "./store";
import {
  ApiBatchRoute,
  ApiRoute,
  ClientType,
  client,
  generateRoute,
  generateRoutes,
  unwrapRoute,
  unwrapRoutes,
} from "./utils";

export type ApiQueryConfig = SWRConfiguration & {
  immutable?: boolean;
};

export const defaultConfig: ApiQueryConfig = {
  dedupingInterval: 30_000,
  errorRetryCount: 3,
  errorRetryInterval: 5_000,
} as const;

export function useApiGet<TEntity>(
  route: ApiRoute,
  clientType: ClientType,
  config?: ApiQueryConfig,
) {
  useApiStore();

  const swr = config?.immutable ? useSWRImmutable : useSWR;
  return swr(
    () => {
      if (!client()) throw new Error();
      if (!unwrapRoute(route) || !generateRoute(route, clientType))
        throw new Error();
      return unwrapRoute(route);
    },
    async (route) => {
      const result = await client()!.get<TEntity>(
        generateRoute(route, clientType),
      );
      return result.data;
    },
    config ?? defaultConfig,
  );
}

export function useBatchApiGet<TEntity>(
  routes: ApiBatchRoute,
  clientType: ClientType,
  config: ApiQueryConfig & { withError: true },
): SWRResponse<{ result?: TEntity; error?: unknown }[]>;

export function useBatchApiGet<TEntity>(
  routes: ApiBatchRoute,
  clientType: ClientType,
  config?: ApiQueryConfig & { withError?: false },
): SWRResponse<TEntity[]>;

export function useBatchApiGet<TEntity>(
  routes: ApiBatchRoute,
  clientType: ClientType,
  config?: ApiQueryConfig & { withError?: boolean },
) {
  const { withError = false, immutable = false, ...rest } = config ?? {};

  useApiStore();

  const swr = immutable ? useSWRImmutable : useSWR;
  return swr(
    () => {
      if (!client()) throw new Error();
      if (
        !unwrapRoutes(routes).length ||
        !generateRoutes(routes, clientType).length
      )
        throw new Error();
      return unwrapRoutes(routes);
    },
    (routes) => {
      return Promise.all(
        generateRoutes(routes, clientType).map(async (route) => {
          try {
            const result = await client()!
              .get<TEntity>(route)
              .then((result) => result.data);

            if (withError) {
              return { result: result, error: undefined };
            }

            return result;
          } catch (error) {
            if (withError) {
              return { data: undefined, error: error };
            }

            throw error;
          }
        }),
      );
    },
    {
      ...defaultConfig,
      ...rest,
    },
  );
}

export function useServerPagedApiGet<TEntity>(
  route: ApiRoute,
  clientType: ClientType,
  limit: number = 10,
  queryParams: Record<string, string | boolean | number> = {},
  config?: ApiQueryConfig,
) {
  useApiStore();

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [tokens, setTokens] = useState<Record<number, string>>({ 0: "" });

  const swr = config?.immutable ? useSWRImmutable : useSWR;
  const { data, ...response } = swr(
    () => {
      if (!client()) throw new Error();
      if (!unwrapRoute(route) || !generateRoute(route, clientType))
        throw new Error();

      const token = tokens[currentPage] ?? "";
      const params = Object.keys(queryParams)
        .map((key) => `&${key}=${queryParams[key]}`)
        .join("");
      return (
        `${unwrapRoute(route)}?limit=${limit}&nextPageToken=${token}` + params
      );
    },
    (route) =>
      client()!.get<DataPageModel<TEntity>>(generateRoute(route, clientType)),
    /*async (route) => {
      const result = await client()!.get<DataPageModel<TEntity>>(
        generateRoute(route, clientType),
      );

      setTotalPages(Math.ceil(result.data.totalItemsCount / limit));
      setTokens((state) => ({
        ...state,
        [currentPage + 1]: result.data.paginationToken ?? "",
      }));

      return result.data.items;
    },*/
    config ?? defaultConfig,
  );

  useEffect(() => {
    if (!data) return;

    setTotalPages(Math.ceil(data.data.totalItemsCount / limit));
    setTokens((state) => ({
      ...state,
      [currentPage + 1]: data.data.paginationToken ?? "",
    }));
  }, [data]);

  return {
    ...response,
    data: data?.data.items,
    canReset: !response.isLoading && !response.isValidating && currentPage > 0,
    canNext:
      !response.isLoading &&
      !response.isValidating &&
      currentPage < totalPages - 1,
    canPrev: !response.isLoading && !response.isValidating && currentPage > 0,
    prevPage: () => {
      if (!response.isLoading && !response.isValidating && currentPage > 0) {
        setCurrentPage((state) => state - 1);
      }
    },
    nextPage: () => {
      if (
        !response.isLoading &&
        !response.isValidating &&
        currentPage < totalPages - 1
      ) {
        setCurrentPage((state) => state + 1);
      }
    },
    reset: () => {
      if (!response.isLoading && !response.isValidating && currentPage != 0) {
        setCurrentPage(0);
      }
    },
  };
}

export function usePagedApiGet<TEntity>(
  route: ApiRoute,
  clientType: ClientType,
  config?: ApiQueryConfig,
) {
  useApiStore();

  const swr = config?.immutable ? useSWRImmutable : useSWR;
  return swr(
    () => {
      if (!client()) throw new Error();
      if (!unwrapRoute(route) || !generateRoute(route, clientType))
        throw new Error();
      return unwrapRoute(route);
    },
    async (baseRoute) => {
      const route = generateRoute(baseRoute, clientType);
      return await getPagedApiResults<TEntity>(route);
    },
    config ?? defaultConfig,
  );
}

export function useBatchPagedApiGet<TEntity>(
  routes: ApiBatchRoute,
  clientType: ClientType,
  config?: ApiQueryConfig,
) {
  useApiStore();

  const swr = config?.immutable ? useSWRImmutable : useSWR;
  return swr(
    () => {
      if (!client()) throw new Error();
      if (
        !unwrapRoutes(routes).length ||
        !generateRoutes(routes, clientType).length
      )
        throw new Error();
      return unwrapRoutes(routes);
    },
    (routes) =>
      Promise.all(
        generateRoutes(routes, clientType).map(getPagedApiResults<TEntity>),
      ),
    config ?? defaultConfig,
  );
}

async function getPagedApiResults<TEntity>(route: string) {
  let nextToken: string | undefined;
  let nextPage = false;
  let page = 1;
  let results: TEntity[] = [];

  do {
    let pageParam = "";
    const separator = route.includes("?") ? "&" : "?";
    if (nextToken) {
      pageParam = `${separator}nextPageToken=${nextToken}`;
    } else if (page > 1) {
      pageParam = `${separator}page=${page}`;
    }
    const pagedRoute = `${route}${pageParam}`;

    const data = await client()!.get<
      DataPageModel<TEntity> | PagedReportDataModel<TEntity>
    >(pagedRoute);

    if ("data" in data.data) {
      results = results.concat(data.data.data);
      page++;
    }

    if ("items" in data.data) {
      results = results.concat(data.data.items);
      nextToken = data.data.paginationToken;
    }

    nextPage = data.data.hasNextPage;
  } while (nextPage);

  return results;
}

type ApiCompleteFunc<TResponse = any> =
  | (() => void)
  | ((response: TResponse) => void);
type ApiErrorFunc = (error: any) => void;

export function useApi<TResponse = any, TRequest = any>() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(undefined);
  const [data, setData] = useState<TResponse | undefined>(undefined);
  const { cache } = useSWRConfig();

  const client = useApiStore((state) => state.client);
  const notyf = useNotyf();

  const onRequest = useCallback(
    (
      method?: Method,
      route?: ApiRoute,
      clientType?: ClientType,
      request?: TRequest,
      onComplete?: ApiCompleteFunc<TResponse>,
      onError?: ApiErrorFunc,
    ) => {
      if (
        !client ||
        !method ||
        !clientType ||
        !generateRoute(route, clientType)
      ) {
        return;
      }

      const process = async () => {
        setIsLoading(true);

        try {
          const result = await client.request<TResponse>({
            method: method,
            url: generateRoute(route, clientType),
            data: request,
          });

          if (method === "DELETE") {
            cache.delete(unwrapRoute(route));
          }

          setError(undefined);
          setData(result.data);

          onComplete?.(result.data);
        } catch (error) {
          setError(error);
          setData(undefined);

          onError?.(error);

          if (error instanceof AxiosError) {
            const message = error.response?.data?.detail ?? error.message;
            notyf.error(message);
          }
        }

        setIsLoading(false);
      };

      process();
    },
    [client, notyf, setIsLoading, setError, setData, cache.delete],
  );

  return {
    isLoading,
    error,
    data,
    onRequest,
  };
}

export function useApiAsync<TResponse = any, TRequest = any>() {
  const {
    isLoading,
    error,
    data,
    onRequest: baseRequest,
  } = useApi<TResponse, TRequest>();

  const onRequest = useCallback(
    (
      method?: Method,
      route?: ApiRoute,
      clientType?: ClientType,
      request?: TRequest,
    ) =>
      new Promise<TResponse>((res, rej) => {
        baseRequest(method, route, clientType, request, res, rej);
      }),
    [baseRequest],
  );

  return {
    isLoading: isLoading,
    error: error,
    data: data,
    onRequest: onRequest,
  };
}

export type ApiGetFunc<TResponse = any> = (
  route: ApiRoute,
) => Promise<TResponse>;

export function useApiGetAsync<TResponse>(clientType: ClientType) {
  const {
    isLoading,
    error,
    data,
    onRequest: baseRequest,
  } = useApi<TResponse>();

  const onRequest: ApiGetFunc<TResponse> = useCallback(
    (route: ApiRoute) =>
      new Promise<TResponse>((res, rej) => {
        baseRequest("GET", route, clientType, undefined, res, rej);
      }),
    [clientType, baseRequest],
  );

  return {
    isLoading: isLoading,
    error: error,
    data: data,
    onRequest: onRequest,
  };
}

export type ApiDeleteFunc = (route: ApiRoute) => Promise<void>;

export function useApiDeleteAsync(clientType: ClientType) {
  const { isLoading, error, onRequest: baseRequest } = useApi();

  const onRequest: ApiDeleteFunc = useCallback(
    (route: ApiRoute) =>
      new Promise<void>((res, rej) => {
        baseRequest("DELETE", route, clientType, undefined, res, rej);
      }),
    [clientType, baseRequest],
  );

  return { isLoading, error, onRequest };
}

export type ApiPutFunc<TResponse = any, TRequest = any> = (
  route: ApiRoute,
  request: TRequest,
) => Promise<TResponse>;

export function useApiPutAsync<TResponse = any, TRequest = any>(
  clientType: ClientType,
) {
  const {
    isLoading,
    error,
    data,
    onRequest: baseRequest,
  } = useApi<TResponse, TRequest>();

  const onRequest: ApiPutFunc<TResponse, TRequest> = useCallback(
    async (route: ApiRoute, request: TRequest) =>
      new Promise<TResponse>((res, rej) => {
        baseRequest("PUT", route, clientType, request, res, rej);
      }),
    [clientType, baseRequest],
  );

  return { isLoading, error, data, onRequest };
}

export type ApiPostFunc<TResponse = any, TRequest = any> = (
  request: TRequest,
) => Promise<TResponse>;

export function useApiPostAsync<TResponse = any, TRequest = any>(
  route: ApiRoute,
  clientType: ClientType,
) {
  const {
    isLoading,
    error,
    data,
    onRequest: baseRequest,
  } = useApi<TResponse, TRequest>();

  const onRequest: ApiPostFunc<TResponse, TRequest> = useCallback(
    async (request: TRequest) =>
      new Promise<TResponse>((res, rej) => {
        baseRequest("POST", route, clientType, request, res, rej);
      }),
    [route, clientType, baseRequest],
  );

  return { isLoading, error, data, onRequest };
}
